import { Button, Input, Popconfirm, Tooltip } from 'antd'
import { useState, useEffect, useRef } from 'react'
import {
  chatState,
  chatAction,
  chatSideBarState,
  chatSideBarAction,
  ChatType,
} from '@/store'
import { message } from '@/App'
import { useThrottleFn, useHover, useClickAway } from 'ahooks'
import { useSnapshot } from 'valtio'
import { deleteHistory, uploadHistory, deleteAllHistory } from '@/api/chat'
import { Clock, X, Check, PencilLine, Trash2 } from 'lucide-react'
import { cn } from '@/utils/css'
import AgentMessageIcon from '@/assets/images/chat/agent_message_list.svg?react'
import ChatMessageIcon from '@/assets/images/chat/chat_message_list.svg?react'

import SidebarTop from './top'

const List = ({ item, filter }: any) => {
  const [isEdit, setIsEdit] = useState(false)
  const { sessionId } = useSnapshot(chatSideBarState)
  const [sessionName, setSessionName] = useState(item.sessionName)
  const ref = useRef(null)
  const isHovering = useHover(ref)

  useClickAway(
    () => {
      if (isEdit) {
        setIsEdit(false)
      }
    },
    ref,
    ['mousedown', 'touchstart'],
  )

  const isActive = item.sessionId === sessionId
  const Reg = new RegExp(filter, 'g')

  const onChangeName = () => {
    uploadHistory(item.sessionId, { title: sessionName })
      .then(() => {
        setIsEdit(false)
        chatSideBarAction.getHistoryRecord()
        message.success('修改成功')
      })
      .catch(() => {
        console.log('修改失败')
      })
  }

  return (
    <div
      ref={ref}
      className={cn(
        'relative cursor-pointer h-10 text-[#494C4F] rounded-lg flex items-center px-2 py-3 hover:bg-[#1c1f2308] hover:pr-16',
        isActive && 'bg-brand/10',
        isEdit && 'pr-16 border border-[#0077fa] bg-white hover:bg-white',
      )}
      key={item.sessionId}
    >
      {item.agent_type != null && item.agent_type === 'chat' ? (
        <ChatMessageIcon />
      ) : (
        <AgentMessageIcon />
      )}
      <div className="w-full">
        {isEdit ? (
          <Input
            className="p-0 text-xs h-8 border-none focus-within:shadow-none pl-2"
            autoFocus
            value={sessionName}
            onChange={e => {
              setSessionName(e.target.value)
            }}
            onPressEnter={onChangeName}
          />
        ) : (
          <div
            className="h-10 text-[#494C4F] rounded-lg flex items-center px-2 py-3"
            onClick={() => {
              chatSideBarAction.changeSelectHistory(item)
            }}
          >
            <div
              className="line-clamp-1 overflow-hidden w-full leading-5"
              dangerouslySetInnerHTML={{
                __html: filter
                  ? item.sessionName.replace(
                      Reg,
                      `<span style="color: #1946B9;">${filter}</span>`,
                    )
                  : item.sessionName || '新的对话',
              }}
            />
          </div>
        )}
      </div>

      <div
        className="absolute right-0 bg-transparent top-[6px] px-2 py-[2px]"
        style={{
          display: isHovering && !isEdit ? 'block' : 'none',
        }}
      >
        {item.agent_type != null && item.agent_type === 'chat' && (
          <Tooltip placement="top" title="编辑">
            <Button
              icon={<PencilLine size={16} strokeWidth={1.2} />}
              size="small"
              type="text"
              className="text-xs text-[#494C4F] cursor-pointer"
              onClick={() => {
                setIsEdit(true)
              }}
            />
          </Tooltip>
        )}
        <Tooltip placement="top" title="删除">
          <Popconfirm
            title="删除后无法恢复，是否继续删除？"
            onConfirm={() =>
              deleteHistory(item.sessionId)
                .then(() => {
                  chatSideBarAction.getHistoryRecord()
                  message.success('删除成功')
                  if (isActive) {
                    chatSideBarAction.createNewSession()
                  }
                })
                .catch(() => {
                  console.log('修改失败')
                })
            }
            okText="删除"
            cancelText="取消"
          >
            <Button
              icon={<Trash2 size={16} strokeWidth={1.2} />}
              size="small"
              type="text"
              className="text-xs text-[#494C4F] cursor-pointer"
            />
          </Popconfirm>
        </Tooltip>
      </div>

      {isEdit && (
        <div className="absolute right-0 bg-transparent top-[6px] px-2 py-[2px]">
          <Button
            icon={<Check size={16} strokeWidth={1.2} />}
            size="small"
            type="text"
            onClick={onChangeName}
          />

          <Button
            icon={<X size={16} strokeWidth={1.2} />}
            size="small"
            type="text"
            onClick={() => {
              setIsEdit(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default function SideBar() {
  const { historySessions } = useSnapshot(chatSideBarState)
  const [filterSessionList, setFilterSessionList] = useState<any[]>([])
  const [filter, setFilter] = useState('')

  const addSession = () => {
    if (chatSideBarState.chatType === ChatType.chat && !chatState.sessionId) {
      message.info('当前已是新对话')
    } else {
      chatSideBarAction.createNewSession()
    }
  }

  const { run } = useThrottleFn(
    filter => {
      if (!filter) {
        setFilterSessionList(chatSideBarState.historySessions)
        setFilter('')
      } else {
        setFilter(filter)
      }
      const filterSessions = chatSideBarState.historySessions
        .map(item => {
          if (item?.sessionName.includes(filter)) {
            return item
          }
          return false
        })
        .filter(Boolean)
      setFilterSessionList(filterSessions)
    },
    { wait: 500 },
  )

  useEffect(() => {
    run('')
  }, [historySessions, run])

  return (
    <div className="flex-shrink-0 w-[220px] flex flex-col align-center h-full overflow-hidden z-20 bg-gradient-to-b from-[#FAFCFF] to-[#EDF3FF] border-l border-[#1C1F230F]">
      <SidebarTop
        onSearch={value => {
          run(value)
        }}
        onNewChat={addSession}
      />
      <div className="flex items-center justify-between text-base font-medium px-4 mb-1">
        <div className="flex items-center">
          <Clock strokeWidth={2} size={16} className="mr-1" />
          <span>对话记录</span>
        </div>
        <Popconfirm
          title="将清空对话记录，是否继续删除？"
          onConfirm={() =>
            deleteAllHistory()
              .then(() => {
                chatSideBarAction.getHistoryRecord()
                message.success('清空对话记录成功')
                chatSideBarAction.createNewSession()
              })
              .catch(() => {
                console.log('deleteAllHistory 失败')
              })
          }
          okText="删除"
          cancelText="取消"
        >
          <div className="text-xs text-[#77797B] cursor-pointer items-end">
            <Tooltip placement="top" title="清空对话记录">
              清空
            </Tooltip>
          </div>
        </Popconfirm>
      </div>
      <div className="flex-1 min-h-0 mr-1 overflow-hidden overflow-y-auto">
        <div className="pl-4 pr-3 pb-2 text-xs space-y-1">
          {filterSessionList.map(item => {
            return <List key={item.sessionId} item={item} filter={filter} />
          })}
        </div>
      </div>
    </div>
  )
}
